import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import { ComicPanel, ComicButton, ComicHeading } from '../components/ComicPanel';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-900">
        <ComicPanel className="w-full max-w-md p-8">
          <ComicHeading as="h1" className="text-3xl text-center mb-8" data-testid="login-title">
            Sign In
          </ComicHeading>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-yellow-500 rounded-none"
                placeholder="you@example.com"
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
                Password
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-yellow-500 rounded-none pr-10"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="text-right">
              <Link 
                to="/forgot-password" 
                className="text-sm text-slate-400 hover:text-yellow-500 transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <ComicButton 
              type="submit" 
              variant="primary" 
              className="w-full" 
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </ComicButton>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-400">
              Don't have an account?{' '}
              <Link to="/signup" className="text-yellow-500 hover:text-yellow-400 font-bold" data-testid="signup-link">
                Sign Up
              </Link>
            </p>
          </div>
        </ComicPanel>
      </div>
    </Layout>
  );
};

export default Login;
